import { defineStore } from 'pinia';
import { hasClientPortalPermission } from '~/lib/clientPortalPermissions';
import { canSendMessageDraft } from '~/lib/messageAttachments';
import { toMillis } from '~/lib/timestamps';
import type { Message, MessageAttachment } from '~/types/models';
import { buildClientPortalDataScopeKey, isCurrentClientPortalDataScope } from '~/lib/clientPortalDataScope';

export const useClientPortalMessagesStore = defineStore('clientPortalMessages', {
    state: () => ({
        messages: [] as Message[],
        scopeKey: null as string | null,
        loading: false,
        error: null as string | null,
    }),

    actions: {
        _threadId(clientId: string): string {
            return `client:${clientId}`;
        },

        _sortMessages(messages: Message[]): Message[] {
            return [...messages].sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
        },

        _isPortalMessage(message: Message): boolean {
            return !message.channel || message.channel === 'app';
        },

        async _authHeaders(): Promise<Record<string, string>> {
            const { $auth } = useNuxtApp();
            const authUser = $auth.currentUser;

            if (!authUser) {
                throw new Error('You must be signed in to load messages.');
            }

            return {
                authorization: `Bearer ${await authUser.getIdToken()}`,
            };
        },

        async fetchThread(tenantId: string, clientId: string): Promise<void> {
            const portalStore = useClientPortalStore();
            const scopeKey = buildClientPortalDataScopeKey({ tenantId, clientId, previewClientId: portalStore.previewingAsClientId });
            if (this.scopeKey !== scopeKey) {
                this.messages = [];
                this.scopeKey = scopeKey;
            }
            this.loading = true;
            this.error = null;

            try {
                if (!hasClientPortalPermission(portalStore.currentAccess, 'viewMessages')) {
                    this.messages = [];
                    return;
                }

                const response = await $fetch<{ messages: Message[] }>('/api/client-portal/messages', {
                    query: {
                        tenantId,
                        clientId,
                        ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                    },
                    headers: await this._authHeaders(),
                });

                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) {
                    this.messages = this._sortMessages(
                        (Array.isArray(response.messages) ? response.messages : [])
                            .filter((message) => this._isPortalMessage(message)),
                    );
                }
            } catch (error) {
                if (!isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) return;
                this.error = `Error loading messages: ${error}`;
                console.error(this.error);
                this.messages = [];
            } finally {
                if (isCurrentClientPortalDataScope(this.scopeKey, scopeKey)) this.loading = false;
            }
        },

        async sendClientReply(params: {
            tenantId: string;
            clientId: string;
            clientName?: string;
            content: string;
            attachments?: MessageAttachment[];
        }): Promise<void> {
            const { $auth } = useNuxtApp();
            const portalStore = useClientPortalStore();
            const scopeKey = this.scopeKey;
            this.error = null;

            if (!hasClientPortalPermission(portalStore.currentAccess, 'sendMessages')) {
                throw new Error('You do not have permission to send portal messages.');
            }

            const trimmed = params.content.trim();
            const attachments = (params.attachments || []).slice(0, 5);
            if (!canSendMessageDraft(trimmed, attachments.length)) {
                throw new Error('Add a message or attachment before sending.');
            }

            const authUser = $auth.currentUser;
            if (!authUser?.uid) {
                throw new Error('You must be logged in to send messages.');
            }

            const response = await $fetch<{ message: Message }>('/api/client-portal/messages', {
                method: 'POST',
                headers: await this._authHeaders(),
                body: {
                    tenantId: params.tenantId,
                    clientId: params.clientId,
                    clientName: params.clientName,
                    content: trimmed,
                    attachments,
                    ...(portalStore.previewingAsClientId ? { preview: '1' } : {}),
                },
            });

            if (!scopeKey || this.scopeKey !== scopeKey) return;
            this.messages = this._sortMessages([
                ...this.messages,
                response.message || {
                    id: `pending-${Date.now()}`,
                    tenantId: params.tenantId,
                    threadId: this._threadId(params.clientId),
                    clientId: params.clientId,
                    clientName: params.clientName || 'Client',
                    content: trimmed,
                    attachments,
                    direction: 'client_to_tenant',
                    channel: 'app',
                    senderId: authUser.uid,
                    senderName: authUser.displayName || authUser.email || 'Client',
                    senderAvatar: authUser.photoURL || '',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                },
            ]);
        },
    },
});
